import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlmCurtailmentPlanDetailQuery } from '../../../../types/generated-types';
import BorderedSection from '../../shared/borderedSection';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import '../ui/plm.css';
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tab-panel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const PlmCurtailmentPlanDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const [tabValue, setTabValue] = React.useState(0);
  const { setTitle, setTitlePath } = useInjectableComponents();

  const { data, loading, error } = usePlmCurtailmentPlanDetailQuery({
    variables: { _id: id! },
  });

  // Extract the type for curtailment actions from the query result
  type PlmCurtailmentAction = NonNullable<
    NonNullable<typeof data>['plmCurtailmentPlanById']
  >['curtailmentActions'][number];

  const isSmall = useIsSmallScreen();

  React.useEffect(() => {
    setTitle(isSmall ? 'View PLM Curtailment Plan' : 'View Curtailment Plan');
    setTitlePath('/plmCurtailmentPlan');

    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isSmall]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const curtailmentActionsColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'deviceType',
      header: 'Device Type',
    },
    {
      accessorKey: 'estimatedReductionKW',
      header: 'Estimated Reduction (kW)',
    },
    {
      accessorKey: 'targetDevices',
      header: 'Target Devices',
      Cell: ({ row }) => {
        if (!row.original) return '';
        return Array.isArray(row.original.targetDevices)
          ? row.original.targetDevices.join(', ')
          : '';
      },
    },
  ];

  if (loading)
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  if (error) return <div>Error loading curtailment plan</div>;
  if (!data?.plmCurtailmentPlanById)
    return <div>Curtailment plan not found</div>;

  const plan = data.plmCurtailmentPlanById;

  return (
    <Box p={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Tooltip title="Back to List">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/plmCurtailmentPlan')}
          >
            Back to PLM Events
          </Button>
        </Tooltip>
        {can('update', 'PlmCurtailmentPlan') && (
          <Tooltip title="Edit">
            <IconButton
              onClick={() => navigate(`/plmCurtailmentPlan/${id}/edit`)}
              sx={{ ml: 2 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={1} className="plm-grid-container">
          <Grid item xs={12}>
            <BorderedSection title="Plan Details">
              <Grid container className="plm-inner-grid-container">
                <Grid item xs={12}>
                  <List className="plm-ul" dense={false}>
                    <ListItem>
                      <b>Name:</b>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plan.name}
                      />
                    </ListItem>
                    <ListItem>
                      <b>Description:</b>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plan.description}
                      />
                    </ListItem>
                    <ListItem>
                      <b>Estimated Reduction:</b>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plan.estimatedReductionKW + ' kW'}
                      />
                    </ListItem>
                    <ListItem>
                      <b>Duration:</b>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plan.durationMinutes + ' minutes'}
                      />
                    </ListItem>
                    <ListItem>
                      <b>Applicable Seasons:</b>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plan.applicableSeasons?.join(', ') ?? ''}
                      />
                    </ListItem>
                    <ListItem>
                      <b>Organization:</b>
                      <ListItemText
                        className="plm-listItem-text"
                        primary={plan.organization?.name}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </BorderedSection>
          </Grid>
        </Grid>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', m: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="secondary"
              textColor="secondary"
              sx={{
                '& .MuiTab-root:hover': {
                  borderBottom: '2px solid lightblue',
                  paddingBottom: '10px',
                },
              }}
            >
              <Tab label="Curtailment Actions" />
            </Tabs>
          </Box>
          <MaterialReactTable
            columns={curtailmentActionsColumns}
            data={
              plan.curtailmentActions?.filter(
                (action): action is PlmCurtailmentAction => !!action,
              ) ?? []
            }
            enableColumnFilters={false}
            enableColumnActions={false}
            enableColumnResizing={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableRowActions={false}
            enableRowSelection={false}
            enableTopToolbar={false}
            muiTableBodyRowProps={{ hover: false }}
            muiTableHeadCellProps={{
              sx: {
                fontWeight: 'bold',
                fontSize: '14px',
                p: '0.5rem',
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                fontSize: '14px',
                p: '0.5rem',
              },
            }}
          />
        </TabPanel>
      </Box>
    </Box>
  );
};
